import { toPaise, toRupees } from '@kortex/shared';
import { computeSettle, type TicketDto } from '@kortex/pos-local';

export interface BillEstimate {
  net: number;
  tax: number;
  roundOff: number;
  total: number;
}

/**
 * The bill-preview figures under the discount box.
 *
 * Before Phase 3 this was a float pro-ration that could differ from the
 * final invoice by up to a rupee; it now runs the same shared GST engine
 * the settle uses (both modes), so the preview equals the printed bill to
 * the paisa. The old pro-ration survives only as the fallback for inputs
 * the engine refuses (e.g. a discount typed larger than the ticket).
 */
export function estimateBill(ticket: TicketDto | undefined, discountRupees: number): BillEstimate {
  if (!ticket || ticket.lines.length === 0) return { net: 0, tax: 0, roundOff: 0, total: 0 };
  try {
    const { totals } = computeSettle(
      ticket.lines.map((line) => ({
        unitPricePaise: toPaise(line.unitPrice),
        quantityMilli: Math.round(line.quantity * 1000),
        discountPctBp: Math.round(line.discountPct * 100),
        gstRateBp: Math.round(line.gstRate * 100),
      })),
      toPaise(Math.max(0, discountRupees)),
      false,
    );
    return {
      net: toRupees(totals.subtotal),
      tax: toRupees(totals.tax),
      roundOff: toRupees(totals.roundOff),
      total: toRupees(totals.total),
    };
  } catch {
    const net = Math.max(0, ticket.totals.subtotal - discountRupees);
    const factor = ticket.totals.subtotal > 0 ? net / ticket.totals.subtotal : 0;
    const tax = ticket.totals.tax * factor;
    const total = Math.round(net + tax);
    return { net, tax, roundOff: total - (net + tax), total };
  }
}
